<template>
  <apexchart ref="chart" height="100%" :options="chartOptions" :series="chartSeries"></apexchart>
</template>

<script>
import { mapState } from 'vuex';
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { computeAmmoVsRealized } from '@/utils/computeAmmoVsRealized.js';
import {
  categoryXAxisOptions,
  latestCategoryZoom,
  manualChartPanOptions,
} from '@/utils/manualChartDays.js';

const ONE_STEP_SERIES = ['Passo · abertura', 'Passo · pico', 'Passo · remanescente'];

function fmtUSD(value) {
  const amount = Number(value) || 0;
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pointText(point) {
  if (!point) return '—';
  return `${fmtUSD(point.total)} <span style="color:#94a3b8;">long ${fmtUSD(point.long)} · short ${fmtUSD(point.short)}</span>`;
}

function chartFromContext(component, chartContext) {
  return chartContext
    || component.$refs?.chart?.chart
    || component.$refs?.chart
    || null;
}

export default {
  name: 'EvolAmmoChart',
  props: {
    logs: { type: Array, default: () => [] },
    rawData: { type: Array, default: () => [] },
    aggregatedData: { type: Array, default: () => [] },
  },
  computed: {
    ...mapState(['targetMarginPct']),

    allLogs() {
      if (this.logs && this.logs.length > 0) return this.logs;
      if (this.rawData && this.rawData.length > 0) return this.rawData;
      if (this.aggregatedData && this.aggregatedData.length > 0) return this.aggregatedData;
      return this.$store?.state?.eventLogs || [];
    },

    ammo() {
      return computeAmmoVsRealized(this.allLogs, { targetMarginPct: this.targetMarginPct });
    },

    daySignature() {
      const days = this.ammo.days;
      if (!days.length) return '';
      return `${days.length}:${days[0].dayKey}:${days[days.length - 1].dayKey}`;
    },

    chartSeries() {
      const days = this.ammo.days;
      const total = (key) => days.map((day) => (day[key] ? day[key].total : null));
      return [
        { name: 'Fechado', type: 'column', data: days.map((day) => day.closed) },
        { name: 'Abertura', type: 'line', data: total('open') },
        { name: 'Pico', type: 'line', data: total('peak') },
        { name: 'Remanescente', type: 'line', data: total('remaining') },
        { name: 'Passo · abertura', type: 'line', data: total('oneStepOpen'), hidden: true },
        { name: 'Passo · pico', type: 'line', data: total('oneStepPeak'), hidden: true },
        { name: 'Passo · remanescente', type: 'line', data: total('oneStepRemaining'), hidden: true },
      ];
    },

    chartOptions() {
      const self = this;
      const margin = this.ammo.targetMarginPct;
      return {
        ...defaultChartOptions,
        chart: {
          ...defaultChartOptions.chart,
          ...manualChartPanOptions(),
          id: 'evol-ammo-vs-realized',
          type: 'line',
          stacked: false,
          events: {
            mounted(chartContext) {
              self.hideOneStep(chartContext);
              self.applyLatestXWindow(chartContext);
            },
            beforeResetZoom() {
              return self.latestResetZoomWindow();
            },
          },
        },
        title: {
          text: 'Munição contra realizado',
          align: 'left',
          style: { color: '#f1f1f1', fontSize: '14px', fontWeight: 600 },
        },
        subtitle: {
          text: `Já no alvo no snapshot Spot (p × q, margem ${margin.toFixed(2)}%). Um passo na legenda.`,
          align: 'left',
          style: { color: '#94a3b8', fontSize: '11px' },
        },
        colors: ['#e66801', '#38bdf8', '#4ade80', '#c084fc', '#7dd3fc', '#86efac', '#d8b4fe'],
        fill: { opacity: [0.85, 1, 1, 1, 1, 1, 1] },
        stroke: {
          curve: 'straight',
          width: [0, 2, 2.5, 2, 1.5, 1.5, 1.5],
          dashArray: [0, 0, 0, 0, 5, 5, 5],
        },
        markers: { size: [0, 3, 4, 3, 2, 2, 2], hover: { size: 5 }, strokeWidth: 0 },
        xaxis: categoryXAxisOptions(this.ammo.days.map((day) => day.label)),
        yaxis: {
          min: 0,
          decimalsInFloat: 0,
          title: { text: 'USD' },
          labels: { formatter: (value) => fmtUSD(value) },
        },
        plotOptions: {
          bar: { columnWidth: '42%' },
        },
        dataLabels: { enabled: false },
        legend: { ...defaultChartOptions.legend },
        tooltip: {
          shared: true,
          intersect: false,
          custom({ dataPointIndex }) {
            return self.tooltip(dataPointIndex);
          },
        },
        noData: {
          text: 'Sem fechamento nem snapshot no período',
          style: { color: '#94a3b8' },
        },
      };
    },
  },
  watch: {
    daySignature() {
      this.$nextTick(() => this.applyLatestXWindow());
    },
  },
  methods: {
    hideOneStep(chartContext) {
      const chart = chartFromContext(this, chartContext);
      if (!chart || typeof chart.hideSeries !== 'function') return;
      ONE_STEP_SERIES.forEach((name) => chart.hideSeries(name));
    },
    applyLatestXWindow(chartContext) {
      const chart = chartFromContext(this, chartContext);
      const zoom = latestCategoryZoom(this.ammo.days.length);
      if (!chart || !zoom || typeof chart.zoomX !== 'function') return;
      chart.zoomX(zoom.start, zoom.end);
    },
    latestResetZoomWindow() {
      const zoom = latestCategoryZoom(this.ammo.days.length);
      if (!zoom) return undefined;
      return { xaxis: { min: zoom.start, max: zoom.end } };
    },
    tooltip(dataPointIndex) {
      const day = this.ammo.days[dataPointIndex];
      if (!day) return '';
      const closed = day.closed == null
        ? 'Sem fechamento neste dia'
        : `${fmtUSD(day.closed)} <span style="color:#94a3b8;">close long ${fmtUSD(day.closeLong)} · close short ${fmtUSD(day.closeShort)}</span>`;
      const ammoBlock = day.hasSnapshot
        ? `
            <div style="margin-top: 6px;">Já no alvo</div>
            <div style="padding-left: 10px; line-height: 1.55;">
              Abertura: ${pointText(day.open)}<br/>
              Pico: ${pointText(day.peak)}<br/>
              Remanescente: ${pointText(day.remaining)}
            </div>
            <div style="margin-top: 6px;">A um passo</div>
            <div style="padding-left: 10px; line-height: 1.55;">
              Abertura: ${pointText(day.oneStepOpen)}<br/>
              Pico: ${pointText(day.oneStepPeak)}<br/>
              Remanescente: ${pointText(day.oneStepRemaining)}
            </div>`
        : '<div style="margin-top: 6px; color: #94a3b8;">Sem snapshot neste dia</div>';
      return `
        <div style="background-color: #1e1e1e; color: #fff; padding: 10px 14px; border-radius: 6px; border: 1px solid #444; font-size: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); min-width: 280px;">
          <div style="font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #444; padding-bottom: 4px; color: #38bdf8;">
            ${day.label}
          </div>
          <div>Fechado: ${closed}</div>
          ${ammoBlock}
        </div>
      `;
    },
  },
};
</script>
